import React from "react";
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { Tooltip } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { format } from "date-fns";
import { numberFormat } from "../../util/currency-formatter";
import { toDecimal, roundValue } from "../../util/decimal-utils";
import { formatAccountingErrorMessage } from "../../util/accounting-error-formatter";
import '../sync-contacts-modal/sync-contacts-modal.scss';

/**
 * Resolves the gross amount inclusive of GST for a row item.
 * @param {Object} row - The invoice or bill row
 * @param {number|string} baseAmount - Base ex-tax amount
 * @returns {number|string}
 */
const resolveAmountInclGst = (row, baseAmount) => {
    if (row.amountInclGst !== undefined && row.amountInclGst !== null) {
        return row.amountInclGst;
    }
    if (row.totalAmount !== undefined && row.totalAmount !== null) {
        return row.totalAmount;
    }
    if (row.total !== undefined && row.total !== null) {
        return row.total;
    }
    if (row.grossAmount !== undefined && row.grossAmount !== null) {
        return row.grossAmount;
    }
    return roundValue(toDecimal(baseAmount || 0).times(toDecimal(1.1)), 2);
};

/**
 * Returns column definitions for the Invoice / Bill synchronization table.
 *
 * @param {Object} options
 * @param {Array} options.invoices - Invoice/Bill rows list
 * @param {Array<string|number>} options.selectedIds - Currently selected row IDs
 * @param {boolean} options.syncing - Sync in-progress flag
 * @param {Function} options.handleSelectAll - Select-all checkbox handler
 * @param {Function} options.handleSelectRow - Single-row selection handler
 * @param {'invoice'|'bill'} options.entityType - Entity type
 * @param {string} [options.propProjectId] - Locked project ID if opened in project scope
 * @param {string} [options.billTab] - Current bill subtab ('supplier'|'contractor')
 * @param {Object} [options.syncLogs] - Current sync batch status logs
 * @param {Object} [options.persistentSyncStatus] - Persisted backend sync status records
 * @param {Array} [options.accountsList] - Platform accounts metadata list
 * @param {Array} [options.taxRatesList] - Platform tax rates metadata list
 * @returns {Array<Object>} CustomTable column configurations
 */
export const getPushStatusValue = (row, syncLogs = {}, persistentSyncStatus = {}) => {
    const log = syncLogs[row?.id];
    if (log) {
        return log.status || '';
    }
    const persistent = persistentSyncStatus[row?.id] || (row?.uId && persistentSyncStatus[row?.uId]);
    if (persistent?.isSynced || persistent?.syncStatus === 'success') {
        return 'success';
    }
    if (persistent?.syncStatus === 'failed' || persistent?.syncStatus === 'error') {
        return 'error';
    }
    if (persistent?.syncStatus) {
        return persistent.syncStatus;
    }
    return '';
};

export const getSyncInvoicesColumns = ({
    invoices,
    selectedIds,
    syncing,
    handleSelectAll,
    handleSelectRow,
    entityType,
    propProjectId,
    billTab,
    syncLogs = {},
    persistentSyncStatus = {},
    accountsList = [],
    taxRatesList = []
}) => {
    const columns = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    checked={invoices.length > 0 && selectedIds.length === invoices.length}
                    onChange={handleSelectAll}
                    disabled={syncing}
                    data-cy="sync-invoices-select-all"
                />
            ),
            field: "select",
            class: "text-center table-select-col",
            render: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                    disabled={syncing}
                    data-cy={`sync-invoices-select-${row.id}`}
                />
            )
        }
    ];

    if (entityType === 'invoice') {
        if (!propProjectId) {
            columns.push({ title: "Project", field: "projectName", render: (row) => row.projectName || '-' });
        }
        columns.push(
            { title: "Claim Number", field: "claimNumber", render: (row) => row.claimNumber || '-' },
            { title: "Date", field: "date", render: (row) => row.date ? format(new Date(row.date), 'dd/MM/yyyy') : '—' },
            { title: "Invoice Number", field: "invoiceNumber", render: (row) => <strong>{row.invoiceNumber || '-'}</strong> },
            { title: "Retention Held", field: "retentionAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionAmount || 0) },
            { title: "Retention Released", field: "retentionClaimAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionClaimAmount || 0) },
            {
                title: "Amount",
                field: "approved",
                class: "text-end fw-bold",
                render: (row) => numberFormat(row.approved || 0)
            },
            {
                title: "Amount (Incl. GST)",
                field: "amountInclGst",
                class: "text-end fw-bold",
                render: (row) => numberFormat(resolveAmountInclGst(row, row.approved))
            },
            {
                title: "Status",
                field: "status",
                render: (row) => (
                    <span className={`badge ${row.status === 'APPROVED' ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {row.status}
                    </span>
                )
            }
        );
    } else {
        if (!propProjectId) {
            columns.push({ title: "Project", field: "projectName", render: (row) => row.projectName || '-' });
        }
        columns.push(
            { title: "Ref Number", field: "reference", render: (row) => <strong>{row.reference || '-'}</strong> },
            { title: "PO Ref", field: "poNumber", render: (row) => <span className="text-secondary small">{row.fId || row.poNumber || '-'}</span> },
            { title: billTab === 'contractor' ? "Contractor" : "Supplier", field: "vendorName", render: (row) => <span className="text-truncate d-inline-block text-truncate-max">{row.vendorName}</span> },
            { title: "Date", field: "date", render: (row) => <span className="small">{row.date ? format(new Date(row.date), 'dd/MM/yyyy') : '—'}</span> }
        );
        if (billTab === 'contractor') {
            columns.push(
                { title: "Retention Held", field: "retentionAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionAmount) },
                { title: "Retention Released", field: "retentionReleasedAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionReleasedAmount) }
            );
        }
        columns.push(
            { title: "Amount", field: "amount", class: "text-end fw-bold", render: (row) => numberFormat(row.amount) },
            {
                title: "Amount (Incl. GST)",
                field: "amountInclGst",
                class: "text-end fw-bold",
                render: (row) => numberFormat(resolveAmountInclGst(row, row.amount))
            }
        );
    }

    columns.push({
        title: "Push Status",
        field: "pushStatus",
        class: "text-center align-middle",
        customSort: (row) => getPushStatusValue(row, syncLogs, persistentSyncStatus),
        render: (row) => {
            const log = syncLogs[row.id];
            if (log) {
                return (
                    <>
                        {log.status === 'pending' && (
                            <Tooltip
                                title="Queued"
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <HourglassEmptyIcon className="sync-status-icon sync-status-icon--pending" />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'syncing' && (
                            <Tooltip
                                title="Pushing to accounting platform..."
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <Spinner animation="border" size="sm" className="text-primary sync-spinner-sm" />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'success' && (
                            <Tooltip
                                title={
                                    <div className="text-center">
                                        <div>Synced Successfully</div>
                                        <div className="small text-white-50 mt-1">
                                            Last synced: {format(new Date(), 'dd/MM/yyyy, hh:mm a')}
                                        </div>
                                    </div>
                                }
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <CheckCircleIcon className="sync-status-icon sync-status-icon--success" />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'error' && (
                            <Tooltip
                                title={<div className="sync-error-tooltip-content">{formatAccountingErrorMessage(log.msg, { accountsList, taxRatesList })}</div>}
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{
                                    style: { zIndex: 99999 }
                                }}
                            >
                                <span className="d-inline-flex align-items-center sync-error-link text-danger">
                                    <CancelIcon className="sync-status-icon sync-status-icon--error" />
                                    <span className="ms-1 small fw-semibold">View Error</span>
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'cancelled' && (
                            <Tooltip
                                title={<div className="sync-error-tooltip-content">{log.msg || 'Cancelled before processing'}</div>}
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{
                                    style: { zIndex: 99999 }
                                }}
                            >
                                <span className="d-inline-flex align-items-center">
                                    <DoNotDisturbIcon className="sync-status-icon sync-status-icon--cancelled" />
                                    <span className="ms-1 small fw-semibold sync-status-text--cancelled">Cancelled</span>
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            }

            const persistent = persistentSyncStatus[row.id] || (row.uId && persistentSyncStatus[row.uId]);
            if (persistent?.isSynced || persistent?.syncStatus === 'success') {
                return (
                    <Tooltip
                        title={
                            <div className="text-center">
                                <div>Synced Successfully</div>
                                {persistent?.lastSyncedAt && (
                                    <div className="small text-white-50 mt-1">
                                        Last synced: {format(new Date(persistent.lastSyncedAt), 'dd/MM/yyyy, hh:mm a')}
                                    </div>
                                )}
                            </div>
                        }
                        placement="top"
                        arrow
                        followCursor={true}
                        PopperProps={{ style: { zIndex: 99999 } }}
                    >
                        <span>
                            <CheckCircleIcon className="sync-status-icon sync-status-icon--success" />
                        </span>
                    </Tooltip>
                );
            }

            if (persistent?.syncStatus === 'failed') {
                return (
                    <Tooltip
                        title={<div className="sync-error-tooltip-content">{formatAccountingErrorMessage(persistent.errorDetails?.message || persistent.errorDetails?.error || persistent.errorDetails || 'Sync failed', { accountsList, taxRatesList })}</div>}
                        placement="top"
                        arrow
                        followCursor={true}
                        PopperProps={{ style: { zIndex: 99999 } }}
                    >
                        <span className="d-inline-flex align-items-center sync-error-link text-danger">
                            <CancelIcon className="sync-status-icon sync-status-icon--error" />
                            <span className="ms-1 small fw-semibold">Error</span>
                        </span>
                    </Tooltip>
                );
            }

            return <span className="text-muted">—</span>;
        }
    });

    return columns;
};
